import type { GameEngine } from '../game/engine';
import type { Frog,Vehicle,Platform,Enemy,Home,GameState,DeathCause,GameEvent } from '../game/state';
import { divePhase,mouthOpen,type DivePhase } from '../game/platforms';
import type { GraphicsSettings,GraphicsMode } from '../settings/GraphicsSettings';

export type FrozenFrog=Readonly<Omit<Frog,'hop'>> & {readonly hop:Readonly<NonNullable<Frog['hop']>>|null};
export interface RenderSnapshot {
  readonly state:GameState;readonly resumeState:GameState;readonly frame:number;readonly stateFrame:number;readonly score:number;
  readonly highScore:number;readonly level:number;readonly lives:number;readonly homeCount:number;
  readonly deathCause:DeathCause|null;readonly lastBonus:number;readonly frog:FrozenFrog;
  readonly vehicles:readonly Readonly<Vehicle>[];
  readonly platforms:readonly (Readonly<Platform>&{readonly phase:DivePhase;readonly mouthOpen:boolean})[];
  readonly enemies:readonly Readonly<Enemy>[];readonly homes:readonly Readonly<Home>[];
  readonly lady:Readonly<{platformId:string;offset:number;active:boolean}>|null;
  readonly timer:Readonly<{frames:number;beats:number;ratio:number}>;
}
// Detached DTO: renderer code has no reference to live mutable gameplay data,
// input queues, callbacks, timers or methods that can advance the simulation.
export function captureSnapshot(e:GameEngine):RenderSnapshot{
  return {state:e.state,resumeState:e.resumeState,frame:e.frame,stateFrame:e.stateFrame,score:e.score,highScore:e.highScore,level:e.level,lives:e.lives,homeCount:e.homeCount,
    deathCause:e.deathCause,lastBonus:e.lastBonus,frog:{...e.frog,hop:e.frog.hop?{...e.frog.hop}:null},
    vehicles:e.vehicles.map(v=>({...v})),platforms:e.platforms.map(p=>({...p,phase:divePhase(p,e.frame),mouthOpen:mouthOpen(e.frame,p.phaseOffset)})),
    enemies:e.enemies.map(n=>({...n})),homes:e.homes.map(h=>({...h})),lady:e.lady?{...e.lady}:null,
    timer:{frames:e.timer.frames,beats:e.timer.beats,ratio:e.timer.ratio}};
}
export function snapshotLadyPosition(lady:RenderSnapshot['lady'],platforms:RenderSnapshot['platforms']):{x:number;y:number}|null{
  if(!lady?.active)return null;
  const p=platforms.find(p=>p.id===lady.platformId);
  return p?{x:p.x+lady.offset,y:p.y}:null;
}
export interface RenderFrame { title:boolean;wallTime:number;reducedMotion:boolean }
export interface GameRenderer {
  readonly mode:GraphicsMode;
  draw(snapshot:RenderSnapshot,frame:RenderFrame):void;
  events(events:readonly GameEvent[],snapshot:RenderSnapshot):void;
  configure(settings:GraphicsSettings):void;
  suspend():void;resume():void;dispose():void;
}
